import { Injectable, ComponentFactoryResolver, Injector, ApplicationRef, EmbeddedViewRef, ComponentRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Notification {
  id: number;
  user: string;
  type: string;
  message: string;
  timestamp: string;
  read: boolean;
  data?: any;
}

export interface CustomNotification {
  id: string;
  message: string;
  type: 'success' | 'info' | 'warning' | 'error';
  duration?: number;
  icon?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationContainer: HTMLElement | null = null;
  private activeNotifications: Set<string> = new Set();

  constructor(
    private http: HttpClient,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef
  ) {
    this.createNotificationContainer();
    this.requestNotificationPermission();
  }

  /**
   * Request permission for native browser notifications
   */
  private requestNotificationPermission(): void {
    if (!('Notification' in window)) {
      console.warn('🔔 [NOTIFICATION] Browser does not support notifications');
      return;
    }

    if (Notification.permission === 'default') {
      console.log('🔔 [NOTIFICATION] Requesting notification permission...');
      Notification.requestPermission().then(permission => {
        console.log('🔔 [NOTIFICATION] Permission result:', permission);
      });
    } else {
      console.log('🔔 [NOTIFICATION] Permission status:', Notification.permission);
    }
  }

  private createNotificationContainer(): void {
    console.log('🔔 [NOTIFICATION] Creating notification container');
    this.notificationContainer = document.createElement('div');
    this.notificationContainer.id = 'custom-notification-container';
    this.notificationContainer.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 12px;
      max-width: 400px;
      pointer-events: none;
    `;
    document.body.appendChild(this.notificationContainer);
    console.log('🔔 [NOTIFICATION] Container created and added to body');
  }

  show(message: string, type: 'success' | 'info' | 'warning' | 'error' = 'info'): void {
    console.log('🔔 [NOTIFICATION] Creating notification:', { message, type });
    const notificationId = this.generateId();
    const notification = this.createNotificationElement(notificationId, message, type);

    if (this.notificationContainer) {
      console.log('🔔 [NOTIFICATION] Adding notification to container');
      this.notificationContainer.appendChild(notification);
      this.activeNotifications.add(notificationId);

      // Auto-remove after duration
      setTimeout(() => {
        this.removeNotification(notificationId);
      }, 5000);
    } else {
      console.error('🔔 [NOTIFICATION] Container not found!');
    }

    // ❌ DISABLED: Native notifications should ONLY come from WebSocket (critical events)
    // CRUD actions (product added, order updated, etc.) should ONLY show in-app toast
    // this.showNativeNotification(message, type);
  }

  /**
   * 🆕 Show ONLY in-app toast notification (no native)
   * Use this for WebSocket notifications to avoid duplicates with WebPush
   */
  showInAppOnly(message: string, type: 'success' | 'info' | 'warning' | 'error' = 'info'): void {
    console.log('🔔 [NOTIFICATION] Creating IN-APP ONLY notification:', { message, type });
    const notificationId = this.generateId();
    const notification = this.createNotificationElement(notificationId, message, type);

    if (this.notificationContainer) {
      this.notificationContainer.appendChild(notification);
      this.activeNotifications.add(notificationId);

      setTimeout(() => {
        this.removeNotification(notificationId);
      }, 5000);
    } else {
      console.error('🔔 [NOTIFICATION] Container not found!');
    }

    // ❌ NO native notification - WebPush will handle it
  }

  /**
   * 🆕 Show native browser notification (appears in Windows notification center / mobile)
   */
  showNativeNotification(message: string, notificationType: string): void {
    if (!('Notification' in window)) {
      console.warn('🔔 [NOTIFICATION] Native notifications not supported');
      return;
    }

    if (Notification.permission !== 'granted') {
      console.warn('🔔 [NOTIFICATION] Permission not granted, status:', Notification.permission);
      return;
    }

    try {
      const title = this.getTitleForType(notificationType);

      // Add severity badge emoji to message based on type
      let badgedMessage = message;
      if (notificationType === 'CAMERA_VERIFICATION_URGENT' || notificationType === 'SECURITY_ALERT') {
        badgedMessage = '🚨 ' + message;
      } else if (notificationType === 'RECONCILIATION_DANGER') {
        badgedMessage = '⚠️ ' + message;
      } else if (notificationType === 'RECONCILIATION_WARNING') {
        badgedMessage = '⚡ ' + message;
      } else if (notificationType === 'RECONCILIATION_SUCCESS') {
        badgedMessage = '✅ ' + message;
      }

      // Use notification TYPE as tag — browser replaces any previous notification
      // of the same type regardless of message content.
      const dedupeTag = 'notif-type-' + notificationType;

      console.log('🔔 [NOTIFICATION] Creating native notification, tag:', dedupeTag);

      const options: NotificationOptions = {
        body: badgedMessage,
        icon: '/favicon.ico',
        tag: dedupeTag,
        requireInteraction: notificationType === 'CAMERA_VERIFICATION_URGENT' || notificationType === 'SECURITY_ALERT',
        silent: false
      };

      // ✅ Chrome blocks new Notification() when a Service Worker is active.
      // Use ServiceWorkerRegistration.showNotification() as primary path.
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.ready.then(registration => {
          return registration.showNotification(title, options);
        }).then(() => {
          console.log('✅ [NOTIFICATION] Native notification shown via ServiceWorker');
        }).catch(err => {
          console.warn('⚠️ [NOTIFICATION] SW showNotification failed, using fallback:', err);
          this._showNotificationFallback(title, options);
        });
      } else {
        // Fallback: no active Service Worker (Firefox, Safari, dev mode)
        this._showNotificationFallback(title, options);
      }
    } catch (error) {
      console.error('❌ [NOTIFICATION] Failed to show native notification:', error);
    }
  }

  private _showNotificationFallback(title: string, options: NotificationOptions): void {
    try {
      const nativeNotif = new Notification(title, options);
      nativeNotif.onclick = (event) => {
        event.preventDefault();
        window.focus();
        nativeNotif.close();
      };
      setTimeout(() => nativeNotif.close(), 10000);
      console.log('✅ [NOTIFICATION] Native notification shown via Notification API');
    } catch (err) {
      console.error('❌ [NOTIFICATION] Fallback Notification API also failed:', err);
    }
  }

  /**
   * Simple hash function for message deduplication
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  private getTitleForType(type: string): string {
    switch (type) {
      case 'success':
      case 'RECONCILIATION_SUCCESS':
        return '✅ Succès - Stock Management';
      case 'info':
      case 'INFO':
        return 'ℹ️ Info - Stock Management';
      case 'warning':
      case 'RECONCILIATION_WARNING':
        return '⚠️ Attention - Stock Management';
      case 'error':
        return '🚨 Alerte - Stock Management';
      case 'CAMERA_VERIFICATION_URGENT':
        return '🚨 URGENT - Vérification Caméra';
      case 'RECONCILIATION_DANGER':
        return '⚠️ Alerte Réconciliation';
      case 'SECURITY_ALERT':
        return '🛡️ Alerte Sécurité';
      default:
        return 'Stock Management';
    }
  }

  private createNotificationElement(id: string, message: string, type: 'success' | 'info' | 'warning' | 'error'): HTMLElement {
    const notification = document.createElement('div');
    notification.id = id;
    notification.className = `custom-notification custom-notification-${type}`;

    const icon = this.getIconForType(type);
    const color = this.getColorForType(type);

    notification.innerHTML = `
      <div class="notification-content">
        <div class="notification-icon">${icon}</div>
        <div class="notification-message">${message}</div>
        <button class="notification-close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
      <div class="notification-progress"></div>
    `;

    notification.style.cssText = `
      background: ${color.background};
      color: ${color.text};
      border: 1px solid ${color.border};
      border-radius: 16px;
      padding: 16px 20px;
      margin-bottom: 8px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(20px);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.5;
      max-width: 380px;
      min-width: 300px;
      position: relative;
      overflow: hidden;
      pointer-events: auto;
      transform: translateX(100%);
      opacity: 0;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      animation: slideInNotification 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    `;

    // Add hover effects
    notification.addEventListener('mouseenter', () => {
      notification.style.transform = 'translateX(0) scale(1.02)';
      notification.style.boxShadow = '0 25px 80px rgba(0, 0, 0, 0.2), 0 12px 40px rgba(0, 0, 0, 0.15)';
    });

    notification.addEventListener('mouseleave', () => {
      notification.style.transform = 'translateX(0) scale(1)';
      notification.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1)';
    });

    // Add progress bar animation
    setTimeout(() => {
      const progressBar = notification.querySelector('.notification-progress') as HTMLElement;
      if (progressBar) {
        progressBar.style.width = '0%';
        progressBar.style.transition = 'width 5s linear';
        progressBar.style.width = '100%';
      }
    }, 100);

    return notification;
  }

  private getIconForType(type: 'success' | 'info' | 'warning' | 'error'): string {
    switch (type) {
      case 'success': return '✅';
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      default: return 'ℹ️';
    }
  }

  private getColorForType(type: 'success' | 'info' | 'warning' | 'error'): { background: string; text: string; border: string } {
    switch (type) {
      case 'success':
        return {
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.98) 0%, rgba(5, 150, 105, 0.98) 100%)',
          text: '#ffffff',
          border: 'rgba(16, 185, 129, 0.3)'
        };
      case 'info':
        return {
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.98) 0%, rgba(37, 99, 235, 0.98) 100%)',
          text: '#ffffff',
          border: 'rgba(59, 130, 246, 0.3)'
        };
      case 'warning':
        return {
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.98) 0%, rgba(217, 119, 6, 0.98) 100%)',
          text: '#ffffff',
          border: 'rgba(245, 158, 11, 0.3)'
        };
      case 'error':
        return {
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.98) 0%, rgba(220, 38, 38, 0.98) 100%)',
          text: '#ffffff',
          border: 'rgba(239, 68, 68, 0.3)'
        };
      default:
        return {
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.98) 0%, rgba(37, 99, 235, 0.98) 100%)',
          text: '#ffffff',
          border: 'rgba(59, 130, 246, 0.3)'
        };
    }
  }

  private generateId(): string {
    return 'notification-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  private removeNotification(id: string): void {
    const notification = document.getElementById(id);
    if (notification) {
      notification.style.animation = 'slideOutNotification 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards';
      setTimeout(() => {
        notification.remove();
        this.activeNotifications.delete(id);
      }, 300);
    }
  }

  success(message: string): void {
    this.show(message, 'success');
  }

  info(message: string): void {
    this.show(message, 'info');
  }

  warning(message: string): void {
    this.show(message, 'warning');
  }

  error(message: string): void {
    this.show(message, 'error');
  }

  getNotificationsPage(limit: number, offset: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/notifications?limit=${limit}&offset=${offset}`);
  }

  markAsRead(id: number): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/notifications/${id}/read`, {});
  }

  approveUser(userId: number): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/admin/users/${userId}/approve`, {});
  }

  rejectUser(userId: number): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/admin/users/${userId}/reject`, {});
  }

  // Méthode pour envoyer une notification aux administrateurs
  sendNotificationToAdmins(type: string, message: string, data?: any): void {
    const notification = {
      type: type,
      message: message,
      data: data ? JSON.stringify(data) : null,
      timestamp: new Date().toISOString(),
      read: false
    };

    this.http.post(`${environment.apiUrl}/notifications/send-to-admins`, notification).subscribe({
      next: (response) => {
        // Notification sent successfully
      },
      error: (error) => {
        // Handle error silently
      }
    });
  }
}
